import type { ChangeEvent } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Magnifier } from '@gravity-ui/icons';
import { NoSearchResults } from '@gravity-ui/illustrations';
import { Button, Icon, TextInput } from '@gravity-ui/uikit';
import { skipToken } from '@reduxjs/toolkit/query';
import type { MRT_SortingState } from 'material-react-table';

import { useGetEstablishmentsQuery } from 'store/api';

import { NO_DATA_ORG, NO_DATA_ORG_INFO, RESET_SEARCH } from '../constants';

import { AdminTable } from './admin-table';
import { AdminTableLoader, AdminTableLoaderAdaptive } from './admin-table-loader';
import { EstablishmentAddModal } from './modals';
import { useMediaQuery } from '@mui/material';
import { AdminEstablishmentsList } from './admin-establisments-list';

import styles from '../styles.module.scss';
import { ProfileIcon } from 'features/ui-kit/profile-icon';
import { EstablishmentSheetAdd } from './sheets';

export const AdminTableEstablishments = ({ isMobile }: any) => {
	const [inputValue, setInputValue] = useState<string>('');

	const [debouncedInputValue, setDebouncedInputValue] = useState<string>('');
	const [pageSize, setPageSize] = useState<number>(20);
	const [pageIndex, setPageIndex] = useState<number>(0);
	const [sorting, setSorting] = useState<MRT_SortingState>([{ id: 'id', desc: true }]);

	const isAdaptive = useMediaQuery('(max-width: 990px)');

	const {
		data: response,
		refetch,
		isFetching,
		isLoading,
	} = useGetEstablishmentsQuery(
		{
			pageNumber: pageIndex,
			pageSize: pageSize,
			sort: sorting[0]?.desc ? 'DESC' : 'ASC',
			sortField: sorting[0]?.id ?? 'id',
			chars: debouncedInputValue ? debouncedInputValue : undefined,
		} ?? skipToken,
		{ refetchOnMountOrArgChange: true },
	);

	const establishments = response?.content;
	const totalItems = response?.totalElements;

	const handleAddEstablishment = () => {
		refetch();
	};

	useMemo(() => {
		return response?.content;
	}, [response?.content]);

	const inputChange = (event: ChangeEvent<HTMLInputElement>) => {
		const newValue = event.target.value;
		setInputValue(newValue.toLowerCase());
	};

	useEffect(() => {
		const handler = setTimeout(() => {
			setDebouncedInputValue(inputValue);
		}, 500);

		return () => {
			clearTimeout(handler);
		};
	}, [inputValue]);

	return (
		<div className={styles.accounts}>
			{isMobile ? (
				<div className={styles.accounts__title_container}>
					<div className={styles.accounts__title_container__icon}>
						<ProfileIcon />
						<h2 className={styles.accounts__title}>Список учреждений</h2>
					</div>

					<EstablishmentSheetAdd onAdd={handleAddEstablishment} isAdaptive={isAdaptive} />
				</div>
			) : (
				<h2 className={styles.accounts__title}>Список учреждений</h2>
			)}

			<div className={styles.accounts__search}>
				<TextInput
					className={styles.accounts__search__input}
					placeholder="Поиск по названию учреждения"
					size="l"
					startContent={<Icon data={Magnifier} size={16} />}
					hasClear
					value={inputValue}
					onChange={inputChange}
				/>
				{isMobile ? '' : <EstablishmentAddModal onAdd={handleAddEstablishment} isAdaptive={isAdaptive} />}
			</div>

			<div className={styles.accounts}>
				<div className={styles.accounts__table}>
					{isLoading && <>{isAdaptive ? <AdminTableLoaderAdaptive /> : <AdminTableLoader />}</>}

					{!isLoading && establishments?.length !== 0 && (
						<>
							{isAdaptive ? (
								<AdminEstablishmentsList
									data={establishments || []}
									onAdd={handleAddEstablishment}
									pageSize={pageSize}
									setPageSize={setPageSize}
									pageIndex={pageIndex}
									setPageIndex={setPageIndex}
									totalItems={totalItems}
									sorting={sorting}
									setSorting={setSorting}
									isMobile={isMobile}
								/>
							) : (
								<div className={styles.accounts__table__main}>
									<AdminTable
										data={establishments || []}
										onAdd={handleAddEstablishment}
										pageSize={pageSize}
										setPageSize={setPageSize}
										pageIndex={pageIndex}
										setPageIndex={setPageIndex}
										totalItems={totalItems}
										sorting={sorting}
										setSorting={setSorting}
									/>
								</div>
							)}
						</>
					)}

					{!isLoading && establishments?.length == 0 && inputValue?.length !== 0 && (
						<div className={styles.accounts__table__main__empty}>
							<NoSearchResults />
							<h3>{NO_DATA_ORG}</h3>
							<p>{NO_DATA_ORG_INFO}</p>
							<Button onClick={() => setInputValue('')} view="normal" size="l">
								{RESET_SEARCH}
							</Button>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};
